/* ============================================
   QUOTE DASHBOARD — AR / AP Card (receivables half + closeout half)
   Asset: dashboard-ar-ap-card.js

   Extracted from dashboard-main.js on 2026-05-23 as Phase B.2.c of the
   dashboard refactor. The AR/AP card has two semantic halves that
   shipped in two cps:

     - Top half (Phase B.2.c.i): receivables — Invoice row + Payments
       parent row + Payment child rows (method/amount/date).
     - Closeout half (Phase B.2.c.ii): Taxes Remitted toggle + Installer
       Paid toggle. Rep + CFA commission toggles live in a separate
       Commission card at the bottom of the detail panel (queued for
       B.2.d).

   The outer .rqd-detail__arap wrapper, section title, .rqd-detail__arap-
   row inner div, and AR/AP-defer slice/stash logic ALL stay in main.js
   (buildDetailHtml ~L8367-8629). This module returns just the INNER
   HTML chunk between the section title and the close divs.

   Loaded via <script defer> in sections/rep-quote-dashboard.liquid BEFORE
   main.js. Public API attached to window.RQD:

     buildArApTopHalf(q) -> html string
       Returns concatenated HTML for the Invoice row + Payments parent
       + Payment child rows. Caller appends into its own html accumulator
       between sectionTitleHtml('AR / AP', _arDone) and the closeout
       toggles. Online-order payment synthesis + paid-method normalization
       + linked-children payment date aggregation all live here.

   Init contract (RQD.initArApCard called once by main.js at boot):
     esc, formatDate, money, isResidentialOrg, buildToggle — function
       declarations in main, safe to pass by reference per the init-deps-
       var-hoisting-trap memo.
     getIsAdmin — closure-getter (NOT a bare reference). isAdmin is a
       `var` in main that mutates after Auth.getSession() resolves (~L91),
       so a bare reference would capture undefined/false at init time and
       the Void invoice link silently drops out for users authenticated
       after init. Same getter pattern as initLeadDetail / initOrderCards
       / initShippingCards.
   ============================================ */
(function() {
  'use strict';
  window.RQD = window.RQD || {};

  /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — "sold but not paid in full" → the AR/AP invoice
     button becomes "Send Invoice" (Abby emails it to bill-to). Online orders are prepaid at checkout
     so they're never in this state. Mirrors the Payments-row paid math below. */
  window.RQD.isSoldNotFullyPaid = function (q) {
    if (!q || q.source === 'online') return false;
    if (q.lifecycleStatus !== 'sold') return false;
    var payments = q.payments || [];
    var qTotal = parseFloat(q.total || q.quoteAmount || q.amount || 0);
    var totalPaid = 0;
    for (var i = 0; i < payments.length; i++) totalPaid += parseFloat(payments[i].amount) || 0;
    var fullyPaid = qTotal > 0 ? totalPaid >= qTotal : !!q.paid;
    return !fullyPaid;
  };

  // Injected by RQD.initArApCard (from main.js boot)
  var esc, formatDate, money, isResidentialOrg, getIsAdmin, buildToggle;

  function buildArApTopHalf(q) {
    var html = '';

    // Invoiced row — custom single-line row with checkbox + invoice actions
    // (like vendor tracking). Two cases default the row to "n/a" with a
    // green-filled checkbox without locking it (Generate Invoice still
    // works in case the customer asks for one):
    //   1. Residential buyers (Org = Residence / n/a / – etc.)
    //   2. Online Shopify orders — receipt-driven, invoice rarely needed,
    //      but K-12 / university buyers occasionally ask for a paid one
    var inv = q.invoice || {};
    var invDone = !!q.invoiced;
    var invLocked = inv.generated;
    var invInferredNa = !invDone && (isResidentialOrg(q.customerCompany) || q.source === 'online');
    var invRowDone = invDone || invInferredNa;
    var invNaTitle = q.source === 'online'
      ? 'Online order — invoice not typically required. Generate Invoice if requested.'
      : 'Residential — invoice not required';
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Invoice row redesign:
       1. Lock symbol (🔒) dropped; generated invoices show ✓ instead,
          matching every other "done" row on the panel. The semantic
          "this is locked, can't toggle off" was confusing alongside
          the new Void button — the void is the correct undo, not a
          manual checkbox toggle.
       2. Combined date meta on the LEFT: "created MM/DD/YYYY, due
          MM/DD/YYYY" so both timestamps live in one .rqd-toggle__date
          span instead of split across left + right.
       3. Slim italic Void link (admin only) — mirrors the PO void
          pattern. Hidden once the invoice has been sent (sentDate
          present); rep would need to send a manual cancellation
          note in that case. */
    /* CUSTOM_DEALDASHBOARD_BY_CLAUDE (Ryan 2026-08-27) - PHASE 2. `n/a` is a THIRD state here,
       not a flavour of done: an invoice inferred as not-applicable is settled without one ever
       existing, and reporting that as `done` would tell a reader an invoice was raised. */
    html += '<div class="rqd-toggle' + (invRowDone ? ' rqd-toggle--done' : '') + '" data-ref="' + esc(q.reference) + '"'
      + ' data-step="invoice" data-state="' + (invInferredNa ? 'not-applicable' : (invDone ? 'generated' : 'not-generated')) + '"'
      + (invInferredNa ? ' title="' + invNaTitle + '" data-blocked-reason="' + invNaTitle + '"' : '') + '>';
    html += '<div class="rqd-toggle__check' + (invInferredNa ? ' rqd-toggle__check--na' : '') + '" style="cursor:default;">' + (invDone ? '&#10003;' : (invInferredNa ? 'n/a' : '')) + '</div>';
    html += '<span>Invoiced' + (inv.isPaidInvoice ? ' (Paid)' : '') + '</span>';
    if (invDone && q.invoicedDate) {
      var _invMeta = 'created ' + formatDate(q.invoicedDate);
      /* CUSTOM_RFQPORTAL_BY_CLAUDE — Render due date in the same M/DD/YYYY
         format as the created date. inv.dueDateStr is the long-form
         "May 17, 2026" (formatted by the worker for PDF use); inv.dueDate
         is the ISO string we can pipe through formatDate() for the
         short form. Fall back to dueDateStr if dueDate is missing
         (legacy records). */
      if (!inv.isPaidInvoice && (inv.dueDate || inv.dueDateStr)) {
        _invMeta += ', due ' + (inv.dueDate ? formatDate(inv.dueDate) : inv.dueDateStr);
      }
      /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      html += '<span class="rqd-toggle__date">' + _invMeta + '</span>';
    }
    /* CUSTOM_RFQPORTAL_BY_CLAUDE — Void link rendered BEFORE the
       right-aligned action wrapper so it sits left of Preview, same
       position the PO row's Void link uses. Admin only; hidden once
       the invoice has been emailed. */
    if (inv.generated && getIsAdmin() && !inv.sentDate) {
      html += '<button class="rqd-freight-reset-link" data-invoice-action="void" data-ref="' + esc(q.reference) + '" type="button" title="Void this invoice — clears the lifecycle record so you can re-generate or cancel.">Void</button>';
    }
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Invoice actions on the same line
    html += '<div class="rqd-invoice" data-ref="' + esc(q.reference) + '">';
    if (window.RQD.isSoldNotFullyPaid(q)) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-06-24) — Sold but not paid in full → "Send Invoice":
         one click generates the PDF if needed, then opens the Abby composer to the bill-to contact.
         After the first send (inv.sentDate) it reads "Resend Invoice" so reps can chase the balance.
         A Preview link sits alongside once the PDF exists. Routed by data-invoice-mode="send". */
      var _invSent = !!(inv.generated && inv.sentDate);
      var _sendLabel = _invSent ? 'Resend Invoice' : 'Send Invoice';
      // To-do → prominent solid-green (--active). Done (sent) → drop --active so the SOLD card's
      // ghost rule applies (transparent + dark-green #14532d outline + #052e16 ink), identical to the
      // "Fulfilled ✓" button. NO opacity — that's what washed it out before. Preview uses the same
      // .rqd-invoice__generate so it ghosts too.
      var _sendCls = _invSent ? '' : ' rqd-invoice__generate--active';
      html += '<button class="rqd-invoice__link rqd-invoice__generate' + _sendCls + '" data-invoice-mode="send" data-ref="' + esc(q.reference) + '" style="cursor:pointer;">' + _sendLabel + '</button>';
      if (inv.generated && inv.pdfUrl) {
        html += '<a class="rqd-invoice__link rqd-invoice__generate" href="' + esc(inv.pdfUrl) + '" target="_blank" style="margin-left:8px;">Preview</a>';
      }
    } else if (!inv.generated) {
      /* CUSTOM_RFQPORTAL_BY_CLAUDE (Ryan 2026-05-20) — Generate Invoice
         must read as a prominent next-action (dark forest green) when
         the order needs an invoice to collect payment — PO purchases
         today (sb.poReceived). The muted-green default still applies
         to upfront-payment + residential + online orders where invoice
         is optional. */
      var _arApSb = q.soldBasis || {};
      var _invActiveClass = _arApSb.poReceived ? ' rqd-invoice__generate--active' : '';
      html += '<button class="rqd-invoice__link rqd-invoice__generate' + _invActiveClass + '" data-ref="' + esc(q.reference) + '" style="cursor:pointer;">Generate Invoice</button>';
    } else {
      /* CUSTOM_PAIDCOMPOSE_BY_CLAUDE (Ryan 2026-08-31) — ⛔ WITHOUT THIS BUTTON, DECLINING THE
         EMAIL IS A ONE-WAY DOOR. A deal that is paid in full falls out of isSoldNotFullyPaid, so
         the Send / Resend Invoice button above never renders for it — and this branch used to
         offer a Preview link and nothing else. That was fine while the paid invoice always mailed
         itself; now that recording the last payment opens a composer you can decline, a PAID
         invoice can sit generated and unsent with no way to send it. `sentKind` is what makes the
         test honest: a paid invoice prepared over a previously-sent DUE one inherits that one's
         sentDate, so "has it been sent" has to ask WHICH invoice was sent, not merely whether a
         send ever happened. (Records written before sentKind existed fall back to sentDate, which
         is right for them — they were never prepared-and-held.) */
      var _paidUnsent = inv.isPaidInvoice
        && (inv.sentKind ? inv.sentKind !== 'paid' : !inv.sentDate);
      if (_paidUnsent) {
        html += '<button class="rqd-invoice__link rqd-invoice__generate rqd-invoice__generate--active" data-invoice-mode="send-paid" data-ref="' + esc(q.reference) + '" style="cursor:pointer;">Send Paid Invoice</button>';
      }
      // Invoice generated → step is done; Preview link uses the
      // green .rqd-invoice__generate variant (same as Flow A's
      // completed-step pattern).
      html += '<a class="rqd-invoice__link rqd-invoice__generate" href="' + esc(inv.pdfUrl) + '" target="_blank"' + (_paidUnsent ? ' style="margin-left:8px;"' : '') + '>Preview</a>';
    }
    html += '</div>';
    html += '</div>';
    /* END CUSTOM_RFQPORTAL_BY_CLAUDE */

    // Payments — parent/child checkbox system
    var payments = q.payments || [];
    var qTotal = parseFloat(q.total || q.quoteAmount || q.amount || 0);
    // Online Shopify orders are pre-paid at checkout. The worker should
    // synthesize a payment record (q.payments) when displayFinancialStatus
    // is PAID; this defensive fallback covers cases where that didn't
    // land (cached response, slightly out-of-sync state, etc.) so the
    // checkbox + date still render as fully paid.
    if (q.source === 'online' && payments.length === 0 && qTotal > 0) {
      payments = [{
        method: 'Online (Shopify)',
        amount: qTotal,
        date: (q.dateSold || q.createdAt || '').slice(0, 10),
      }];
    }
    var totalPaid = 0;
    for (var pi = 0; pi < payments.length; pi++) totalPaid += parseFloat(payments[pi].amount) || 0;
    var fullyPaid = qTotal > 0 ? totalPaid >= qTotal : !!q.paid;
    // Online orders → always treat as fully paid for the checkbox state.
    if (q.source === 'online') fullyPaid = true;
    var paidPct = qTotal > 0 ? Math.min(Math.round(totalPaid / qTotal * 10) * 10, 100) : 0;
    var fillPct = fullyPaid ? 100 : paidPct;

    // Parent row — checkbox fills proportionally
    /* The payment row's own state. ⚠️ `fullyPaid` is forced true for an online order a few
       lines up, because Shopify holds the money rather than lc.payments - so this reports the
       row as the card DRAWS it, which is the honest thing for a reader comparing the two. */
    html += '<div class="rqd-toggle" style="cursor:default;"'
      + ' data-step="payment" data-state="' + (fullyPaid ? 'paid' : (totalPaid > 0 ? 'part-paid' : 'unpaid')) + '"'
      + ' data-ref="' + esc(q.reference) + '" data-paid-pct="' + fillPct + '">';
    html += '<div class="rqd-payment-check' + (fullyPaid ? ' rqd-payment-check--full' : '') + '">';
    html += '<div class="rqd-payment-check__fill" style="height:' + fillPct + '%"></div>';
    if (fullyPaid) html += '<span class="rqd-payment-check__icon">&#10003;</span>';
    html += '</div>';
    html += '<span>Payments</span>';
    if (totalPaid > 0 && qTotal > 0) {
      if (fullyPaid) {
        /* CUSTOM_RFQPORTAL_BY_CLAUDE — For linked orders (parent +
           balance child(ren)), surface every transaction's date with
           its source order ref so the row reads as a full audit trail
           of when each ledger entry hit (e.g.
           "4/28/2026 (SHOP-1654), 5/12/2026 (SHOP-1683)"). The legacy
           single-date treatment (date the running total crossed
           qTotal) collapses two transactions into one and loses the
           linked-order context. Non-linked orders keep the legacy
           single-date display. */
        var _hasLinkedChildren = Array.isArray(q.balanceChildren) && q.balanceChildren.length > 0;
        if (_hasLinkedChildren) {
          var _paySegs = [];
          // Parent's payment date — first dated entry on q.payments
          // (online: synthesized to q.createdAt; offline: rep-entered).
          var _parentDate = '';
          for (var _pdi = 0; _pdi < payments.length; _pdi++) {
            if (payments[_pdi].date) { _parentDate = payments[_pdi].date; break; }
          }
          if (_parentDate) _paySegs.push(formatDate(_parentDate) + ' (' + esc(q.reference) + ')');
          // Each linked child's payment date. createdAt is the
          // checkout timestamp for online children, which is also the
          // payment moment for Shopify orders.
          q.balanceChildren.forEach(function (_bc) {
            var _bcDate = _bc && _bc.createdAt ? String(_bc.createdAt).slice(0, 10) : '';
            if (_bcDate) _paySegs.push(formatDate(_bcDate) + ' (' + esc(_bc.ref || _bc.orderName || '') + ')');
          });
          if (_paySegs.length > 0) {
            html += '<span class="rqd-toggle__date">' + _paySegs.join(', ') + '</span>';
          }
        } else {
          var runningTotal = 0;
          var paidInFullDate = '';
          for (var fi = 0; fi < payments.length; fi++) {
            runningTotal += parseFloat(payments[fi].amount) || 0;
            if (runningTotal >= qTotal) { paidInFullDate = payments[fi].date || ''; break; }
          }
          if (paidInFullDate) {
            html += '<span class="rqd-toggle__date">' + formatDate(paidInFullDate) + '</span>';
          }
        }
        /* END CUSTOM_RFQPORTAL_BY_CLAUDE */
      } else {
        html += '<span class="rqd-toggle__partial-badge">' + money(totalPaid) + ' of ' + money(qTotal) + '</span>';
      }
    }
    // + Add Payment button — right-aligned on header row, styled like invoice
    // buttons. Disabled (grayed out) once the quote is paid in full so reps
    // don't accidentally tack on extra payment rows. Hidden on online
    // orders entirely (Shopify already collected payment; no AR/AP work
    // for the rep). A passive "Paid via Shopify" label takes its place so
    // the row reads as a completed step.
    html += '<div style="margin-left:auto; padding-right:12px;">';
    /* CUSTOM_OFFLINEINVOICE_BY_CLAUDE (AIOPS f307f08a89ba, Ryan 2026-09-12) — WHICH SHOPIFY
       ORDER PAID THIS QUOTE, ON SCREEN.
       ⛔ THE LINK EXISTED AND WAS INVISIBLE. `wm.shopifyPaidOrder` and
       `lifecycle.soldBasis.shopifyOrderName` have carried SHOP-2052 on JW-1262-B since the
       customer paid; `q.paidOrderName` has been on the row since the Cancel/Refund guard needed
       it. Nothing ever DREW it, so the one question the Shopify "Order placed" mail in orders@
       poses — which deal is this? — could not be answered by opening the deal. That is the
       Vendor-ETA lesson: a read path complete on both ends and no surface between them.
       ⚠️ Offline rows only. An online row IS the order, and says "Paid via Shopify" already. */
    var _paidOrderName = q.source !== 'online' ? (q.paidOrderName || '') : '';
    if (_paidOrderName) {
      html += '<span class="rqd-paid-on-order" title="This quote was paid through a Shopify invoice. '
        + esc(_paidOrderName) + ' is the payment order — the freight on it was priced when the quote was built.">'
        + 'Paid on ' + esc(_paidOrderName) + '</span>';
    }
    if (q.source === 'online') {
      html += '<span class="rqd-paid-via-online">Paid via Shopify</span>';
    } else if (!fullyPaid && getIsAdmin()) {
      /* Once the order is paid in full the button hides completely —
         not greyed-out / disabled. If a payment is later removed or
         adjusted so the order falls back to partially-paid, the next
         detail re-render brings the button back unconditionally.

         CUSTOM_PAIDCOMPOSE_BY_CLAUDE (Ryan 2026-08-31) — and admin-only now. Ryan: *"admins are
         the ones that control bank accounts and cash checks... reps, not so much."* A rep reports
         an incoming payment as a sold basis; entering the money is the admin's. The worker refuses
         it either way (quote-routes.js MONEY_FIELDS), so this is the honest UI for that rule, not
         the rule itself. Reps still SEE every payment row below — commission depends on it. */
      html += '<button type="button" class="rqd-invoice__link rqd-payment-add" data-ref="' + esc(q.reference) + '" style="cursor:pointer;">+ Add Payment</button>';
    }
    html += '</div>';
    html += '</div>';

    // Child payment rows — progressive reveal: method → amount → date.
    // Skipped on online orders since the synthesized Shopify payment isn't
    // editable and showing the method/amount/date selectors would imply
    // the rep needs to fill them in (they don't).
    var paidMethods = ['ACH', 'Wire', 'Check', 'Zelle', 'Cash', 'Card'];
    if (q.source === 'online') paidMethods = [];
    for (var pi2 = 0; pi2 < payments.length && q.source !== 'online'; pi2++) {
      var pmt = payments[pi2];
      var pmtAmt = parseFloat(pmt.amount) || 0;
      // Legacy values from auto-SOLD / link-external paths ('External Order', 'Credit Card',
      // Shopify gateway names) are all card settlements from an AR/AP standpoint — normalize
      // to 'Card' so the dropdown shows a selection instead of blank.
      var pmtMethod = pmt.method && !paidMethods.includes(pmt.method) ? 'Card' : (pmt.method || '');
      var pmtComplete = !!(pmtMethod && pmtAmt > 0 && pmt.date);
      var hasMethod = !!pmtMethod;
      var hasAmount = pmtAmt > 0;

      html += '<div class="rqd-payment-row' + (pmtComplete ? ' rqd-payment-row--complete' : '') + '" data-ref="' + esc(q.reference) + '" data-payment-idx="' + pi2 + '">';

      // Left side: child checkbox + label + date
      html += '<div class="rqd-payment-child-check' + (pmtComplete ? ' rqd-payment-child-check--done' : '') + '">' + (pmtComplete ? '&#10003;' : '') + '</div>';
      html += '<span class="rqd-payment-child-label">Payment ' + (pi2 + 1) + '</span>';
      if (pmtComplete && pmt.date) html += '<span class="rqd-payment-child-date">' + formatDate(pmt.date) + '</span>';

      /* CUSTOM_PAIDCOMPOSE_BY_CLAUDE (Ryan 2026-08-31) — READ-ONLY FOR REPS. The row still shows
         everything (the tick, the label, the date on the left, method + amount here) because a
         rep's commission depends on knowing what has been paid; what they lose is the ability to
         change it. ⛔ Rendered as TEXT rather than disabled inputs: a greyed-out amount box invites
         a rep to ask why they cannot type in it, and the answer — recording money is the admin's
         job — is better told by there being nothing to type into. The worker refuses these fields
         from a non-admin regardless (quote-routes.js MONEY_FIELDS), so a stale page cannot write
         through this either. */
      if (!getIsAdmin()) {
        html += '<div class="rqd-payment-row__fields">';
        html += '<span class="rqd-payment-child-label" style="opacity:0.85;">'
          + (pmtMethod ? esc(pmtMethod) : '&mdash;')
          + (pmtAmt ? ' &middot; ' + money(pmtAmt) : '')
          + '</span>';
        html += '</div>';
        html += '</div>';   // close the row — the admin path closes it below
        continue;
      }

      // Right side: fields group
      html += '<div class="rqd-payment-row__fields">';
      html += '<button type="button" class="rqd-payment-remove" title="Remove payment" aria-label="Remove payment">&times;</button>';
      html += '<select class="rqd-toggle__inline-select rqd-payment-method">';
      html += '<option value="">-- Method --</option>';
      for (var mi = 0; mi < paidMethods.length; mi++) {
        var pm = paidMethods[mi];
        html += '<option value="' + pm + '"' + (pmtMethod === pm ? ' selected' : '') + '>' + pm + '</option>';
      }
      html += '</select>';
      var otherPaid = 0;
      for (var oi = 0; oi < payments.length; oi++) { if (oi !== pi2) otherPaid += parseFloat(payments[oi].amount) || 0; }
      var remaining = Math.max(0, Math.round((qTotal - otherPaid) * 100) / 100);
      var placeholder = remaining > 0 ? money(remaining) : '$0.00';
      html += '<input type="text" class="rqd-toggle__inline-input rqd-payment-amount" placeholder="' + placeholder + '" data-remaining="' + remaining.toFixed(2) + '" value="' + (pmtAmt ? money(pmtAmt) : '') + '"' + (hasMethod ? '' : ' style="display:none;"') + '>';
      html += '<input type="date" class="rqd-toggle__inline-date rqd-payment-date" value="' + esc(pmt.date || '') + '" style="' + (hasAmount ? '' : 'display:none;') + '">';
      html += '<span class="rqd-toggle__saved rqd-payment-saved"></span>';
      html += '</div>';

      html += '</div>';
    }

    /* CUSTOM_ABBY_BY_CLAUDE (Ryan 2026-07-23) — Expected ship date.
       Lives here, next to the balance it exists to collect. Entering it is what
       schedules Abby's balance-request email to the customer 10 days before the
       order ships; until it's set she nudges Ryan weekly (4 times, then stops).

       Shown whenever an offline deal still owes money. The hint line only
       promises the balance email when the deal is actually eligible for it —
       Net 30 / 50/50 Net 30 balances aren't due until 30 days AFTER receipt, so
       those are excluded worker-side and must not be promised here. */
    var _owesMoney = q.source !== 'online' && qTotal > 0 && !fullyPaid;
    var _esd = q.expectedShipDate || '';
    /* Row stays visible once a date exists even after the deal is paid — the ETA
       is still a useful record — but the Abby status drops off, since it's only
       there to answer "is the chase running?" on money we're still owed.
       (Ryan 2026-07-23.) */
    if (_owesMoney || (_esd && q.source !== 'online')) {
      var _bc = q.balanceCollection || {};
      var _fu = q.invoiceFollowUps || {};
      var _isNet30 = /net\s*30/i.test(String(q.terms || ''));
      var _eligible = !_isNet30 && totalPaid > 0;

      /* Abby status suffix — EXCEPTION ONLY (Ryan 2026-08-05). The "enabled"
         half was dropped: it's the default state of every eligible deal, so it
         labelled the normal case and added a line of noise to every row. "On
         hold" stays, because that one is worth noticing — either automated
         sequence has paused itself after the customer wrote back, a human owns
         the thread, and Abby has deliberately gone quiet. Without it, a silent
         Abby and a paused Abby look identical. */
      var _abbyStatus = '';
      if (_owesMoney && (_bc.pausedAt || _fu.stoppedAt)) {
        _abbyStatus = ' — Abby emails on hold';
      }

      /* PARENT row (.rqd-toggle), not a .rqd-payment-row — this is a deal-level
         date, not one of the payment children, so it takes no left indent and its
         control right-aligns with "+ Add Payment" / "Send Invoice" one row up. */
      /* --set drives the ghost/filled treatment on the date input (CSS: the
         .rqd-payment-row--complete ghost block). A row class, not an attribute
         selector on the input: `[value]` only reflects the RENDERED value, so it
         would go stale the moment the rep picks a different date without a
         re-render — and this handler deliberately avoids re-rendering so the
         open card doesn't collapse. saveExpectedShipDate() toggles it live. */
      html += '<div class="rqd-toggle rqd-expected-ship-row' + (_esd ? ' rqd-expected-ship-row--set' : '') + '" style="cursor:default;" data-ref="' + esc(q.reference) + '">';
      html += '<div class="rqd-payment-check' + (_esd ? ' rqd-payment-check--full' : '') + '">';
      html += '<div class="rqd-payment-check__fill" style="height:' + (_esd ? 100 : 0) + '%"></div>';
      if (_esd) html += '<span class="rqd-payment-check__icon">&#10003;</span>';
      html += '</div>';
      html += '<span>Expected ship date' + (_abbyStatus ? '<span class="rqd-abby-status' + (_abbyStatus.indexOf('hold') > -1 ? ' rqd-abby-status--hold' : '') + '">' + esc(_abbyStatus) + '</span>' : '') + '</span>';
      if (_esd) html += '<span class="rqd-toggle__date">' + formatDate(_esd) + '</span>';
      /* "Balance email sent" rides inline after the date instead of in the hint
         line below (Ryan 2026-08-05) — it's a fact about this date, so it reads
         as "— 8/18/2026 — Balance email sent 8/5/2026". Its own span rather than
         appended into the date span: .rqd-toggle__date is nowrap, and gluing ~28
         more characters inside it would squeeze the date input on a narrow
         viewport. Two spans let the browser break BETWEEN them and never mid-
         phrase. Sizing/colour are shared with .rqd-payment-child-date in CSS,
         not copied, so this can't drift from the Payment rows above. */
      if (_esd && _eligible && _bc.balanceEmailSentAt) {
        html += '<span class="rqd-abby-sent">Balance email sent '
          + formatDate(String(_bc.balanceEmailSentAt).slice(0, 10)) + '</span>';
      }
      html += '<div style="margin-left:auto; padding-right:12px; display:flex; align-items:center; gap:8px;">';
      html += '<span class="rqd-toggle__saved rqd-expected-ship-saved"></span>';
      html += '<input type="date" class="rqd-toggle__inline-date rqd-expected-ship-date" value="' + esc(_esd) + '">';
      html += '</div>';
      html += '</div>';

      /* Status line — only when Abby has actually done something, or will. The
         "set this to queue…" prompt was cut (Ryan 2026-07-23): the row's own
         label already says what it is. */
      var _hint = '';
      if (_esd && _eligible) {
        /* The "sent" case moved inline next to the date above, so this line is
           now only the not-yet-sent promise. 10 days mirrors BALANCE_LEAD_DAYS in
           rfq-worker src/email/customer-invoice-notification.js — the theme can't
           import it, so if that number moves, move this string with it. */
        _hint = _bc.balanceEmailSentAt ? '' : 'Balance request queues 10 days prior';
      } else if (!_esd && _eligible && _bc.reminderCount) {
        _hint = 'Awaiting date — reminder ' + _bc.reminderCount + ' of 4 sent';
      } else if (_isNet30) {
        _hint = 'Net 30 — balance invoiced after shipping, no pre-ship request';
      }
      if (_hint) {
        html += '<div class="rqd-expected-ship-hint">' + esc(_hint) + '</div>';
      }
    }
    /* END CUSTOM_ABBY_BY_CLAUDE */


    return html;
  }

  /* CUSTOM_SUPPLIERPAYDIGEST_BY_CLAUDE (Ryan 2026-09-13) — WHAT WE OWE A SUPPLIER, on the card
     that is literally called AR / AP and until today had no payable on it at all. The pay ledger
     has existed since 2026-09-10 and was visible only on a Settings panel and a daily email; Ryan,
     on being shown the first digest: *"surprised it doesn't already exist in the DD"*.

     ⛔ ADMIN ONLY, AND THAT IS NOT A CONVENTION, IT IS THE COST RULE. A supplier payable is a
     COST: what we pay ECORE for the goods, which a sales rep can see nowhere else in this portal
     (lib/deal-feed.js `mayReadFeedCost` draws that line, and `adminText` exists to keep cost off a
     rep's screen). Rendering this to everyone would put the cost of every line on the one card
     every rep opens.

     ⛔ THE DATE IS PICKED, NEVER STAMPED. Every closeout toggle on this screen wrote `todayStr()`
     the moment you ticked it, and filed months of late-recorded payments under the wrong day
     before anyone noticed — Ryan, 2026-09-02: *"it's easy to forget to mark these things on the
     exact day, so sometimes it's best to not"*. Blank is allowed and means "we did not record
     when", which is honest. A plausible wrong date is not. */
  /**
   * @param q      the deal row
   * @param opts   { rows }  the payables to draw. ABSENT means "all of them", which is the
   *               AR / AP fallback; the Purchasing card passes the slice for one vendor.
   *               `showVendor` false drops the supplier's name from the label - on Purchasing it
   *               sits under that vendor's own sub-header and repeating it is noise (Ryan
   *               2026-09-13: *"we don't need it to read 'ECORE Athletic owed', it can just read
   *               'Owed...'"*).
   */
  function buildPayableRows(q, opts) {
    if (!getIsAdmin()) return '';
    opts = opts || {};
    var rows = opts.rows || (q && q.supplierPay) || [];
    /* ⛔ A PAID PO STAYS ON THE CARD, TICKED. Ryan 2026-09-13, on RW-1339-A after marking both POs
       paid: *"how come I don't see the two rows for each of the 2 vendors with the paid checkbox
       marked done?"* They had gone entirely, because the board only ever received the OPEN rows -
       so a supplier who had been paid read identically to a deal that never had a PO. The
       Payments rows one card up do not disappear when the customer pays, and neither should
       these. */
    var paid = opts.paid || (q && q.supplierPaid) || [];
    if (!rows.length && !paid.length) return '';
    var showVendor = opts.showVendor !== false;
    /* ⛔ THE SENT DATE GOES WITH THE VENDOR NAME, because both are redundant for the same reason:
       under a vendor block on Purchasing, the supplier's sub-header and its PO line are directly
       above this row and already say both. Ryan 2026-09-13: *"since the PO line is directly above
       it, we don't need it to read 'PO sent [date]' any longer either... redundant otherwise."*
       ⚠️ The PO NUMBER stays either way - with two POs to one supplier it is the only thing that
       says which of them this row is about. */
    var showSent = opts.showSent === undefined ? showVendor : opts.showSent !== false;
    /* ⛔ AND THE PO NUMBER GOES TOO, with one exception. Ryan 2026-09-13: *"we also don't need the
       PO number listed."* Right in the ordinary case - the vendor block above lists its POs. But a
       supplier with TWO unpaid POs on one deal would then render two rows reading "Owed $X" and
       "Owed $Y" with nothing saying which PO either is, and Mark paid settles ONE of them. So it
       is shown only when it is the thing that tells two rows apart, which is never in the common
       case and always in the one where it matters. */
    var showPo = showVendor || (rows.length + paid.length) > 1;
    var html = '';
    rows.forEach(function(r) {
      /* The age of the debt, from the day the purchase order went out — the same number the
         digest prints, so the two surfaces cannot tell different stories. */
      var days = null;
      if (r.openedAt) {
        var t = Date.parse(String(r.openedAt).slice(0, 10) + 'T12:00:00Z');
        if (isFinite(t)) days = Math.max(0, Math.round((Date.now() - t) / 86400000));
      }
      /* ⛔ THE DATE, NOT A COUNT OF DAYS (Ryan 2026-09-13). "1 day" is a number you have to
         convert back into a date to act on; "PO sent Sep 12" is the fact itself, and it matches
         how every other date on this card reads. The age still drives the COLOUR, because "this
         has been sitting a month" is worth seeing without doing the arithmetic. */
      var sentTxt = r.openedAt ? 'PO sent ' + formatDate(String(r.openedAt).slice(0, 10)) : '';
      var ageCol = days === null ? '' : (days >= 21 ? '#f87171' : (days >= 7 ? '#f59e0b' : ''));
      /* ⛔ THE PAYMENT ROW'S OWN MARKUP, NOT A LOOKALIKE. The first cut hand-rolled the layout
         with inline styles and it read as a foreign control on a familiar card: the button ran to
         the far right of the panel while every field above it stops 12px short, and the inputs
         were the wrong height and colour. `rqd-payment-row__fields` already carries
         `margin-left:auto` AND the 12px right padding that lines the Payments date picker up with
         the buttons one row above, and `rqd-toggle__inline-select` / `__inline-date` are the
         shared field styles. Sharing the rules is the house rule; a second set of declarations
         that happens to match today is the thing that drifts. */
      /* ⛔ A TOP-LEVEL ROW, NOT A CHILD ROW (Ryan 2026-09-13). The first cut used
         `rqd-payment-row` + `rqd-payment-child-check`, which is the indented 14px checkbox a
         Payment 1 / Payment 2 line under Payments wears. This is a peer of Invoiced and Payments,
         not a child of anything, so it takes `rqd-toggle` (no 28px indent, 14px text) and the
         20px `rqd-toggle__check`. ⚠️ `rqd-payment-row__fields` STAYS - it is the only thing that
         right-aligns the controls with the buttons above, and it works inside any flex row. */
      html += '<div class="rqd-toggle rqd-payable" data-ref="' + esc(q.reference) + '" data-po="' + esc(r.poNumber) + '">'
        + '<div class="rqd-toggle__check rqd-payable__check"></div>'
        + '<span>' + (showVendor ? esc(r.vendor || 'Supplier') + ' owed ' : 'Owed ')
        + (r.amount == null ? 'an unrecorded amount' : esc(money(r.amount)))
        + (showPo ? ' - ' + esc(r.poNumber) : '')
        + '</span>'
        + soPoChip(r) // CUSTOM_SOPOCHECK_BY_CLAUDE (ONYX 5dc0b45b4d44)
        + (showSent && sentTxt ? '<span class="rqd-toggle__date"' + (ageCol ? ' style="color:' + ageCol + ';"' : '') + '>' + esc(sentTxt) + '</span>' : '')
        + '<div class="rqd-payment-row__fields">'
        + '<select class="rqd-toggle__inline-select rqd-payable__via" title="How it was paid. Optional.">'
        + '<option value="">-- How? --</option><option value="portal">Portal</option><option value="card">Card</option>'
        + '<option value="ach">ACH</option><option value="check">Check</option><option value="wire">Wire</option><option value="other">Other</option>'
        + '</select>'
        + '<input type="date" class="rqd-toggle__inline-date rqd-payable__date" title="The day the money actually moved. Leave it blank if you are not sure \u2014 a blank is better than a guess.">'
        + '<button type="button" class="rqd-invoice__link rqd-payable__pay" title="Record this purchase order as paid. It drops off the supplier pay digest. Nothing is paid by pressing this.">Mark paid</button>'
        + '<span class="rqd-toggle__saved rqd-payable__status" aria-live="polite"></span>'
        + '</div>'
        + '</div>';
    });
    /* The settled ones last: what is still owed is what needs doing, so it reads first. */
    paid.forEach(function(r) {
      var when = r.paidAt || (r.settledAt ? String(r.settledAt).slice(0, 10) : '');
      var word = r.settledOutcome === 'not-owed' ? 'Not owed' : (r.settledOutcome === 'void' ? 'Voided' : 'Paid');
      /* ⚠️ SAYS ONLY WHAT IT KNOWS. A row settled before the payment detail existed carries no
         date, payer or method, and renders as a bare "Paid" rather than inventing any of them. */
      var detail = [];
      if (when) detail.push(when);
      if (r.paidBy) detail.push('by ' + r.paidBy);
      if (r.paidVia) detail.push(r.paidVia === 'portal' ? 'in the supplier portal' : 'by ' + String(r.paidVia).toUpperCase());
      html += '<div class="rqd-toggle rqd-payable rqd-payment-row--complete" data-ref="' + esc(q.reference) + '" data-po="' + esc(r.poNumber) + '" data-paid="1">'
        + '<div class="rqd-toggle__check rqd-toggle__check--done">&#10003;</div>'
        + '<span>' + (showVendor ? esc(r.vendor || 'Supplier') + ' ' : '')
        + esc(word) + ' ' + (r.amount == null ? '' : esc(money(r.amount)))
        + (showPo ? ' - ' + esc(r.poNumber) : '')
        + '</span>'
        + (detail.length ? '<span class="rqd-toggle__date">' + esc(detail.join(' ')) + '</span>' : '')
        + '</div>';
    });
    return html;
  }

  /* CUSTOM_SOPOCHECK_BY_CLAUDE (ONYX `5dc0b45b4d44`, Ryan 2026-10-02) - THE SUPPLIER'S OWN TOTAL
     DISAGREES WITH OUR PO. SHOP-2118: ECORE's portal said $136.20, our PO $188.04 (freight left
     off), and Ryan only caught it at pay time. The worker holds the row off the pay digest and
     writes `soHeld` / `soGap` / `soTotal` / `soReason` into the row's list metadata; this is the
     red chip that says so on the screen he pays from, and Mark paid asks before recording a held
     row (paying the short or the long figure is the mistake the chip exists to stop). */
  var SO_REASON_WORDS = { 'freight-missing': 'freight left off', 'freight-added': 'freight added', 'tax-added': 'tax added', 'qty-price-drift': 'qty or price differs', 'line-mismatch': 'wrong item' }; // line-mismatch: CUSTOM_SOLINEMISMATCH_BY_CLAUDE
  function soPoChip(r) {
    if (!r || !r.soHeld) return '';
    var gap = Number(r.soGap);
    var lines = r.soReason === 'line-mismatch'; // CUSTOM_SOLINEMISMATCH_BY_CLAUDE (ONYX 15d49a02d054): right total, wrong item
    var gapTxt = isFinite(gap) && Math.abs(gap) >= 0.005 ? (gap < 0 ? '-' : '+') + money(Math.abs(gap)) : '';
    var why = SO_REASON_WORDS[r.soReason] || '';
    var title = 'Their order says ' + (r.soTotal == null ? 'a different figure' : money(r.soTotal))
      + ' and our PO says ' + (r.amount == null ? 'another' : money(r.amount))
      + (why ? ' (' + why + ')' : '') + '. Held off the pay list until a revised order matches.';
    return '<span class="rqd-payable__sopo" title="' + esc(title) + '" style="margin-left:8px;padding:1px 7px;border-radius:9px;background:rgba(248,113,113,0.15);color:#f87171;font-size:12px;font-weight:700;white-space:nowrap;">'
      + (lines ? 'SO lines ≠ PO' : 'SO ≠ PO') + (gapTxt ? ' ' + esc(gapTxt) : '') + (why ? ' · ' + esc(why) : '') + '</span>';
  }

  /* One delegated listener for every payable row on the page. ⚠️ Registered ONCE — `initArApCard`
     runs on every card build, so a listener added there without this guard fires N times and
     posts the settle N times. */
  var _payablesWired = false;
  function wirePayables() {
    if (_payablesWired) return;
    _payablesWired = true;
    document.addEventListener('click', async function(e) {
      var btn = e.target.closest && e.target.closest('.rqd-payable__pay');
      if (!btn) return;
      var row = btn.closest('.rqd-payable');
      if (!row) return;
      var po = row.getAttribute('data-po');
      /* CUSTOM_SOPOCHECK_BY_CLAUDE - a held row asks first; the chip's title is the sentence */
      var _soChip = row.querySelector('.rqd-payable__sopo');
      if (_soChip && !window.confirm((_soChip.getAttribute('title') || 'The supplier order does not match our PO.') + String.fromCharCode(10, 10) + 'Record ' + po + ' as paid anyway?')) return;
      var status = row.querySelector('.rqd-payable__status');
      var dateEl = row.querySelector('.rqd-payable__date');
      var viaEl = row.querySelector('.rqd-payable__via');
      btn.disabled = true;
      var orig = btn.textContent;
      btn.textContent = 'Recording...';
      if (status) status.textContent = '';
      try {
        var body = { poNumber: po, outcome: 'paid' };
        if (dateEl && dateEl.value) body.paidAt = dateEl.value;
        if (viaEl && viaEl.value) body.paidVia = viaEl.value;
        var res = await window.RepPortal.API.post('/supplier-pay/settle', body);
        if (res && res.settled && res.settled.length) {
          /* Mark it done in place rather than re-rendering the board: the row is gone on the next
             load anyway, and a full refresh would collapse the panel the admin is working in.
             ⛔ `--complete` is the SAME class a finished payment row wears, so the ghosting is the
             one the card already does rather than a second opinion about what done looks like. The
             fields are disabled as well as ghosted: a control that still takes input after the
             money is recorded invites a second click nothing will action. */
          /* ⚠️ THE FIELDS GHOST BUT STAY EDITABLE, because that is exactly what a completed
             payment row does one section up - `--complete` re-tints them and nothing is disabled.
             Disabling them was this row having its own opinion about "done", which is the drift
             the shared classes exist to prevent. Only the BUTTON goes inert, the way
             `+ Add Payment` does once the order is paid in full. */
          /* CUSTOM_SUPPLIERPAYDIGEST_BY_CLAUDE (AIOPS `5a3c91c495dd`, Ryan 2026-09-14) — ⛔ MOVE
             THE PAYABLE IN THE CACHED ROW, or the payment un-happens on the next refresh. Ryan:
             *"after the UI finally shows Recorded, a hard refresh shows the payment not
             recorded again."* The worker's write was fine; the board paints from a 6h
             sessionStorage entry that SURVIVES Ctrl+Shift+R, so the row being re-rendered from
             cache still carried the PO under `supplierPay` (still owed) and not under
             `supplierPaid`. The server was right and the screen was reading yesterday.
             ⛔ MOVED BETWEEN THE TWO FIELDS, never deleted: `supplierPaid` is what draws the
             ghosted "Paid" line, and dropping the PO entirely would make a settled purchase
             order read exactly like a deal that never had one — the ambiguity the two-field
             split was introduced to end.
             ⚠ AFTER THE 200, never before: painting first would hide a failed write, which is
             the failure mode this whole card is about. */
          try {
            if (window.RQD && window.RQD.patchCachedQuoteRow) {
              var _settledRef = row.getAttribute('data-ref');
              var _settledPo = po;
              window.RQD.patchCachedQuoteRow(_settledRef, function(q) {
                var moved = (q.supplierPay || []).filter(function(r) { return r && r.poNumber === _settledPo; });
                q.supplierPay = (q.supplierPay || []).filter(function(r) { return !(r && r.poNumber === _settledPo); });
                if (!q.supplierPay.length) delete q.supplierPay;
                if (moved.length) {
                  q.supplierPaid = (q.supplierPaid || []).concat(moved.map(function(r) {
                    /* Only what we actually know. A settle with no date renders a bare "Paid"
                       rather than inventing one — the row builder's own rule. */
                    var out = { poNumber: r.poNumber, vendor: r.vendor, amount: r.amount, openedAt: r.openedAt,
                      settledAt: new Date().toISOString(), settledOutcome: 'paid' };
                    if (dateEl && dateEl.value) out.paidAt = dateEl.value;
                    if (viaEl && viaEl.value) out.paidVia = viaEl.value;
                    return out;
                  }));
                }
              });
            }
          } catch (_) { /* the next real fetch is the backstop; never fail a recorded payment */ }
          row.classList.add('rqd-payment-row--complete');
          var check = row.querySelector('.rqd-payable__check');
          if (check) { check.classList.add('rqd-toggle__check--done'); check.innerHTML = '&#10003;'; }
          /* ⚠️ NOT disabled here: the pre-flight above already disabled it, and only the two
             failure branches put it back. Re-setting it read as the line that made the button
             inert, and deleting it changed nothing - which is exactly what an untested line is. */
          btn.textContent = 'Paid';
          /* CUSTOM_PAIDGHOST_BY_CLAUDE (Ryan 2026-09-19) — ⛔ SUCCESS SAYS NOTHING; FAILURE STILL
             SHOUTS. This read "Recorded, paid 2026-09-19", which repeated two things already on the
             row: the button now says Paid and the date is still sitting in the picker beside it.
             Ryan: *"no need for this text. just ghost out the buttons, and that's my indication it
             was paid. I can read the date right there on the date picker too."*
             ⛔ CLEARED, NOT LEFT ALONE. The element is reused by both failure branches below
             ("Could not record it: …", "Nothing open matched …") and by the "Recording…" that ran
             a moment ago, so leaving the old text would strand a stale word beside a settled row.
             ⛔ AND THE FAILURE MESSAGES ARE UNTOUCHED — a write that silently fails is the whole
             failure mode this card was built around (see the cached-row note above); the ghost is
             the success signal, never the only signal. */
          if (status) status.textContent = '';
        } else {
          btn.disabled = false;
          btn.textContent = orig;
          if (status) status.textContent = 'Nothing open matched — it may already be settled.';
        }
      } catch (err) {
        btn.disabled = false;
        btn.textContent = orig;
        if (status) status.textContent = 'Could not record it: ' + ((err && err.message) ? err.message : 'unknown error');
      }
    });
  }

  function buildArApCloseout(q) {
    var html = '';
    var fin = q.financials || {};

    /* ⛔ ONLY WHAT THE PURCHASING CARD DID NOT DRAW. On nearly every committed deal the payable
       belongs under its vendor on Purchasing; this half is the fallback for the deals that have
       no Purchasing card (online Flow A bucket orders, linked children) and for any payable whose
       vendor has no block there. Ryan 2026-09-13, on being shown that Purchasing is not a
       multi-vendor-only card: *"the AR/AP positioning was wrong all along."*
       ⚠️ ONE ROW, ONE PLACE - the rule the Installer Paid move left written on this card. */
    html += buildPayableRows(q, { rows: (q && q._payablesForArAp) || [], paid: (q && q._paidForArAp) || [] });

    var _arTaxes = parseFloat(fin.taxes || q.taxes || 0);
    if (_arTaxes > 0) {
      html += buildToggle(q.reference, 'taxesRemitted', 'Taxes Remitted', q.taxesRemitted, q.taxesRemittedDate, { editableDate: true });
    }

    /* CUSTOM_DDINSTALL_BY_CLAUDE (Ryan 2026-09-02) - ⛔ INSTALLER PAID MOVED TO THE INSTALLATION
       CARD and is deliberately NOT rendered here as well.

       It sat here because AR/AP is where money owed is ticked off, which is true but left install
       state spread across three cards - the installer and cost on one, the schedule on another, the
       payment here. It now lives with the rest of the job, GATED on the install being marked
       completed, which is the sequence Ryan asked for: *"when that's done, that ungates the
       opportunity to select when the installer was paid."*

       ⚠️ ONE ROW, ONE PLACE. Leaving a copy here would put two controls on one field, on one
       screen, with only one of them carrying the completed-first gate - the drift this panel has
       been consolidated twice to avoid. Taxes Remitted stays: it is genuinely a receivables line
       and has no card of its own. */
    // Rep + CFA commission toggles moved to the dedicated "Commission"
    // card at the bottom of the detail panel — keeping AR/AP focused on
    // customer-facing receivables (invoice + payments + tax + install).

    return html;
  }

  function initArApCard(deps) {
    deps = deps || {};
    esc = deps.esc;
    formatDate = deps.formatDate;
    money = deps.money;
    isResidentialOrg = deps.isResidentialOrg;
    buildToggle = deps.buildToggle;
    getIsAdmin = deps.getIsAdmin || function() { return false; };
    wirePayables();
  }

  window.RQD.buildArApTopHalf = buildArApTopHalf;
  window.RQD.buildArApCloseout = buildArApCloseout;
  window.RQD.buildPayableRows = buildPayableRows;
  /* kept: the AR / AP half calls it with no options, meaning "every payable this deal has that
     nothing else drew". */
  window.RQD.buildArApPayables = function (q, rows) { return buildPayableRows(q, rows ? { rows: rows } : {}); };
  window.RQD.initArApCard = initArApCard;
})();
